import React, { useState } from 'react';
import { X, CheckCircle2, Shield, Send, Building2, User, Mail, Phone, MapPin, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { Language } from '../../types';
import { TRANSLATIONS } from '../../translations';

interface IntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  prefillOpportunityRef?: string;
  prefillCategory?: string;
}

export const IntakeModal: React.FC<IntakeModalProps> = ({
  isOpen,
  onClose,
  language,
  prefillOpportunityRef,
  prefillCategory
}) => {
  const t = TRANSLATIONS[language]?.exploreOpportunities?.formModal || 
            TRANSLATIONS[Language.EN]?.exploreOpportunities?.formModal || {};

  const [role, setRole] = useState<string>('landOwner');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [region, setRegion] = useState('');
  const [category, setCategory] = useState(prefillCategory || 'Property & Sites');
  const [details, setDetails] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [refCode, setRefCode] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const response = await fetch('/api/explore-opportunity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role,
          name,
          email,
          phone,
          region,
          category,
          details,
          prefillOpportunityRef,
          enquiryType: prefillOpportunityRef ? 'EOI' : 'EO'
        }),
      });

      const contentType = response.headers.get('content-type');
      let data: any = null;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Server returned non-JSON response (${response.status}): ${text.slice(0, 80)}`);
      }

      if (response.ok && data && data.success) {
        setRefCode(data.referenceCode || (prefillOpportunityRef ? `EOI-${Math.floor(1000 + Math.random() * 9000)}` : `EO-${Math.floor(1000 + Math.random() * 9000)}`));
        setIsSubmitted(true);
        // Clear confidential form state on successful submission
        setName('');
        setEmail('');
        setPhone('');
        setRegion('');
        setDetails('');
      } else {
        const serverError = data?.error || `Submission failed. Please try again or contact us directly.`;
        setErrorMsg(serverError);
      }
    } catch (err: any) {
      console.error('Submission error:', err);
      setErrorMsg(err.message || 'Network error occurred. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setErrorMsg(null);
    setName('');
    setEmail('');
    setPhone('');
    setRegion('');
    setDetails('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 md:p-8 bg-slate-900 text-white flex items-start justify-between relative">
          <div className="pr-8">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white/10 border border-white/20 text-[10px] uppercase tracking-widest text-blue-300 font-bold mb-3">
              <Shield size={12} className="text-blue-400" />
              Confidential Intake
            </div>
            <h3 className="text-2xl md:text-3xl font-heading uppercase tracking-widest text-white leading-tight">
              {prefillOpportunityRef ? `Express Interest (${prefillOpportunityRef})` : (t.title || 'Confidential Opportunity Inquiry')}
            </h3>
            <p className="text-xs text-slate-300 font-light mt-2 leading-relaxed">
              {t.sub || 'Tell us about your requirements or opportunity. A Le Sector advisor will review your inquiry with total discretion.'}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 overflow-y-auto flex-1 bg-slate-50">
          {isSubmitted ? (
            <div className="py-10 text-center flex flex-col items-center">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-700 flex items-center justify-center rounded-full mb-6 border border-emerald-300">
                <CheckCircle2 size={36} />
              </div>
              <h4 className="text-2xl font-heading uppercase tracking-widest text-slate-900 mb-2">
                {t.successTitle || 'Inquiry Received'}
              </h4>
              <p className="text-sm font-semibold text-blue-600 mb-4 tracking-wider uppercase">
                {(t.successDesc || 'Thank you for reaching out. Reference code: ') + refCode}
              </p>
              <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed mb-8">
                {t.successSub || 'A Le Sector advisor will review your inquiry confidentially and get in touch within 24-48 hours.'}
              </p>
              <button
                onClick={handleReset}
                className="bg-slate-900 text-white px-8 py-3.5 uppercase tracking-widest text-xs font-bold hover:bg-slate-800 transition-colors"
              >
                {t.close || 'Close'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Stakeholder Role */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-700 mb-3">
                  {t.roleLabel || 'Your Primary Role'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: 'landOwner', label: t.roles?.landOwner || 'Property / Land Owner' },
                    { id: 'investor', label: t.roles?.investor || 'Private / Equity Investor' },
                    { id: 'projectOwner', label: t.roles?.projectOwner || 'Project Developer / Owner' },
                    { id: 'expert', label: t.roles?.expert || 'Professional / Specialist' },
                  ].map((r) => (
                    <button
                      type="button"
                      key={r.id}
                      onClick={() => setRole(r.id)}
                      className={`p-3 text-xs font-semibold text-left border transition-all ${
                        role === r.id
                          ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                    {t.name || 'Full Name'} *
                  </label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-slate-900"
                      placeholder="e.g. John Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                    {t.email || 'Email Address'} *
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-slate-900"
                      placeholder="e.g. john@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Phone & Region */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                    {t.phone || 'Phone Number'}
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-slate-900"
                      placeholder="e.g. 0400 000 000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                    {t.region || 'Target Region / Location'}
                  </label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                      type="text"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-slate-900"
                      placeholder="e.g. Greater Sydney, NSW"
                    />
                  </div>
                </div>
              </div>

              {/* Details */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                  {t.details || 'Project Parameters & What You Are Seeking'} *
                </label>
                <textarea
                  required
                  rows={4}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  className="w-full p-3 bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-slate-900"
                  placeholder="Describe your property, capital capability, project requirements, or expertise..."
                />
              </div>

              {/* Privacy Disclaimer */}
              <div className="p-3.5 bg-slate-100 border border-slate-200 flex items-start gap-3">
                <Shield size={18} className="text-slate-500 shrink-0 mt-0.5" />
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  {t.disclaimer || 'Your information is treated with strict confidentiality. No contact details or exact property addresses will be shared publicly or with third parties without your explicit consent.'}
                </p>
              </div>

              {/* Error Message */}
              {errorMsg && (
                <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2.5">
                  <AlertCircle size={18} className="shrink-0 text-rose-600" />
                  <span className="font-medium">{errorMsg}</span>
                </div>
              )}

              {/* Submit CTA */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-slate-900 text-white py-4 uppercase tracking-widest text-xs font-bold hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <span>Submitting...</span>
                    <Loader2 size={16} className="animate-spin" />
                  </>
                ) : (
                  <>
                    {t.submit || 'Submit Inquiry'} <Send size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
